import { FormulaEvaluationHelper } from './formula-evaluation-helper';
import { INumericValuesHashByFieldName, IValuesByFieldName } from '../../../interfaces/grid-row.interface';
import { FormField } from '../../../models/form-builder/form-field.model';
import { parser } from 'mathjs';

export class FormulaEvaluator {
    // PJH - 10/15/2024 - Added for VNEXT-1419 (url builder field)
    public static concatenateValues(formulaEvaluationHelper: FormulaEvaluationHelper, formulaParam: string, hshFieldValues: IValuesByFieldName): string {
        let formula: string = ((formulaParam != null) && (formulaParam.trim() != '') ? formulaParam : ''); // 07-10-2025:  removed the call to .toLowerCase().
        let result = FormulaEvaluator.replaceTokens(formula, hshFieldValues);
        return result;
    }

    public static getTokenNames(inputString) {
        //formula might look like https://collect.omb.gov/site/{SiteId}/structure/folder/{FolderId}/form/{formId}
        //this will return the tokens SiteId, FolderId, FormId

        const regex = /{(.*?)}/g;
        let tokenNames = inputString.match(regex);
        tokenNames = tokenNames.map(match => match.substring(1, match.length - 1)); //remove braces

        return tokenNames;
    }

    // pharvey - 8/3/2023 - simplified it by removing first param and having all hash lookups
    // performed against the hashes in IFormulaEvaluationHelper.FieldNameHashes
    public static evaluateFormula(formulaEvaluationHelper: FormulaEvaluationHelper, formulaParam: string, hshValues: INumericValuesHashByFieldName = null): any {
        const formulaParser: any = parser();
        if (!formulaParser) return null;

        let formula: string = ((formulaParam != null) && (formulaParam.trim() != '') ? formulaParam.toLowerCase() : '');

        // Build a scope object.
        FormulaEvaluator.buildComputationScope(formulaEvaluationHelper, formulaParser.scope, hshValues);

        // Try to evalulate the expression.
        let result: number = 0;

        try {
            result = formulaParser.evaluate(formula);

            if ((result != null) && (!Number.isNaN(result))) {
                result = Math.round(result * formulaEvaluationHelper.RoundTo) / formulaEvaluationHelper.RoundTo;
                formulaEvaluationHelper.CalculationError = null;
            } else
                formulaEvaluationHelper.CalculationError = 'Calcuation error';
        } catch (error) {
            formulaEvaluationHelper.CalculationError = error.toString();
        }

        // Construct and return a result.
        let hshResult = {
            result: result,
            error: formulaEvaluationHelper.CalculationError
        };

        return (hshResult);
    }

    // pharvey - 8/3/2023 - simplified it by removing first param and having all hash lookups
    // performed against the hashes in IFormulaEvaluationHelper.FieldNameHashes
    private static buildComputationScope(formulaEvaluationHelper: FormulaEvaluationHelper, parserScope: Map<string, number>, hshValues: any = null): void {
        let colNameToFormFieldsHash = formulaEvaluationHelper.FieldNameHashes.hshColNameToFormField;
        for (let fieldName in colNameToFormFieldsHash) {
            let formField: FormField = colNameToFormFieldsHash[fieldName];

            if (formField.gridColClientId !== formulaEvaluationHelper.FormField.gridColClientId) {
                if (hshValues && hshValues[fieldName]) {
                    parserScope.set(fieldName, hshValues[fieldName]);
                } else {
                    parserScope.set(fieldName, 0);
                } // if-else
            } // if
        } // for

        // Add scope members for display variable names.
        for (let gridVariableName in formulaEvaluationHelper.FieldNameHashes.hshDisplayVariableNameToFormField) {
            let formField: FormField = formulaEvaluationHelper.FieldNameHashes.hshDisplayVariableNameToFormField[gridVariableName];

            if (formField.gridColClientId !== formulaEvaluationHelper.FormField.gridColClientId) {
                let fieldName: string =
                    formulaEvaluationHelper.FieldNameHashes.hshFieldIdToFieldName[formField.gridColClientId];

                if (hshValues && hshValues[fieldName]) {
                    parserScope.set(gridVariableName, hshValues[fieldName]);
                } else {
                    parserScope.set(gridVariableName, 0);
                } // if-else
            } // if
        }
    }

    // Thank you, Google AI Overview
    private static replaceTokens(template, values): string {
        // The regex here matches any characters between curly braces - spaces included (/\{(\w+)\}/g just matches alphanumeric)
        return template.replace(/\{(.*?)\}/g, (match, token) => {
            // Need to check both token and token.toLowerCase() because some field names get rendered in the Form Builder
            // as CamelCase, whereas in reality the internal name is all lowercase. User's may well type in CamelCase to match
            // what they're seeing in the Form builder
            if (values.hasOwnProperty(token) || values.hasOwnProperty(token.toLowerCase())) {
                let value = values[token] ?? values[token.toLowerCase()];
                if (!value) value = `PARAM "${token}" IS NOT SET`;
                return value;
            } else {
                return match;
            }
        });
    }
}
